// One storefront, two products. LMU TV and AC TV are the same app built for two games
// (the desktop app does the same from one project), so this storefront is one code base
// too: everything that differs between the two lives in this file and in media/index.ts.
//
// Which product a visitor gets is decided once, from where the page is served:
//   lmutv.de / lmutv.com, or /lmutv on the hub            -> LMU TV
//   actv.simracingsolutions.de, or /actv on the hub        -> AC TV
// A per-app build can force it with REACT_APP_APP=lmutv|actv.

export type ProductId = "lmutv" | "actv";

export interface Product {
  id: ProductId;
  /** Backend app slug: the `app` value of plans.ts / plans.php and every /dyn request. */
  app: ProductId;
  name: string;
  /** The game this product broadcasts. */
  game: string;
  /** Path prefix on the hub. */
  path: string;
  /** Canonical public address, without a trailing slash. */
  site: string;
  /** Locale file in public/locales/<lang>/. */
  localeFile: string;
  /** Feature bits of this product in the TV key tenant. */
  standardBit: number;
  proBit: number;
  /** Orders.app id (the entry bit). */
  appId: number;
  /** Release download from the account page. */
  downloadUrl: string;
  /** ClickOnce style download dialogs, kept for old links. Empty: none. */
  downloadIds: string[];
  /** Host that serves the download dialog files. */
  downloadHost: string;
  /** Application manifest name inside a download folder. */
  downloadFile: string;
  discord: string;
  /** localStorage / sessionStorage prefix, so two products on the hub never share a cart. */
  storagePrefix: string;
  /** Sections that exist for this product. The game decides: AC has no instant replay. */
  hasInstantReplay: boolean;
  /** The coded video sales letter (?hero=vsl) exists for LMU TV only. */
  hasVsl: boolean;
}

const PRODUCTS: Record<ProductId, Product> = {
  lmutv: {
    id: "lmutv",
    app: "lmutv",
    name: "LMU TV",
    game: "Le Mans Ultimate",
    path: "/lmutv",
    site: "https://lmutv.de",
    localeFile: "lmutv.json",
    standardBit: 512,
    proBit: 1024,
    appId: 512,
    downloadUrl: "https://downloads.simracingsolutions.de/download.php?branch=release&app=lmutv",
    downloadIds: ["lmutvapp", "lmutvappbeta"],
    downloadHost: "https://lmutv.de",
    downloadFile: "LMUTV.application",
    discord: "https://dsc.gg/LMUTV",
    storagePrefix: "lmutv",
    hasInstantReplay: true,
    hasVsl: true,
  },
  actv: {
    id: "actv",
    app: "actv",
    name: "AC TV",
    game: "Assetto Corsa",
    path: "/actv",
    site: "https://actv.simracingsolutions.de",
    localeFile: "actv.json",
    standardBit: 2048,
    proBit: 4096,
    appId: 2048,
    downloadUrl: "https://downloads.simracingsolutions.de/download.php?branch=release&app=actv",
    downloadIds: [],
    downloadHost: "https://actv.simracingsolutions.de",
    downloadFile: "ACTV.application",
    discord: "https://dsc.gg/LMUTV",
    storagePrefix: "actv",
    hasInstantReplay: false,
    hasVsl: false,
  },
};

const LMUTV_HOST = /(^|\.)lmutv\.de$/i;
const ACTV_HOST = /^actv\./i;

const underPath = (pathname: string, prefix: string): boolean =>
  pathname === prefix || pathname.startsWith(`${prefix}/`);

/** The storefront this page view belongs to, or null for the hub. */
export function detectProduct(
  hostname: string = window.location.hostname,
  pathname: string = window.location.pathname,
  forced: string | undefined = process.env.REACT_APP_APP
): { id: ProductId; basename: string } | null {
  if (forced === "lmutv" || forced === "actv") return { id: forced, basename: "/" };
  if (LMUTV_HOST.test(hostname)) return { id: "lmutv", basename: "/" };
  if (ACTV_HOST.test(hostname)) return { id: "actv", basename: "/" };
  if (underPath(pathname, PRODUCTS.actv.path)) return { id: "actv", basename: PRODUCTS.actv.path };
  if (underPath(pathname, PRODUCTS.lmutv.path)) return { id: "lmutv", basename: PRODUCTS.lmutv.path };
  return null;
}

const detected = detectProduct();

/** The product of this page view. Falls back to LMU TV, which only matters in tests. */
export const product: Product = PRODUCTS[detected?.id ?? "lmutv"];

/** Router basename of the storefront: "/" on its own host, its path prefix on the hub. */
export const productBasename: string = detected?.basename ?? PRODUCTS.lmutv.path;

export const isActv = product.id === "actv";

/** "AC TV Pro" / "AC TV Standard". */
export const editionName = (edition: "standard" | "pro"): string =>
  `${product.name} ${edition === "pro" ? "Pro" : "Standard"}`;

/** Key for browser storage, namespaced per product. */
export const storageKey = (name: string): string => `${product.storagePrefix}${name}`;
